import React, { useState, useEffect } from 'react';
import { Modal } from 'bootstrap';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { updateLoanDetails } from '../../services/loanDetailsService';

// Form schema
const loanEditSchema = z.object({
  loanAmount: z.number().min(0.01, 'Loan amount must be greater than 0'),
  interestRate: z.number().min(0, 'Interest rate cannot be negative').max(100, 'Interest rate cannot exceed 100%'),
  dueDate: z.string().min(1, 'Due date is required'),
  commencementDate: z.string().min(1, 'Commencement date is required'),
  adminFee: z.number().min(0, 'Admin fee cannot be negative'),
  reason: z.string().optional(),
  invoiceNumber: z.string().optional(),
});

type LoanEditFormData = z.infer<typeof loanEditSchema>;

interface LoanEditModalProps {
  applicationId: string;
  loan: {
    loanAmount?: number;
    interestRate?: number;
    dueDate?: string;
    commencementDate?: string;
    adminFee?: number;
    customerName?: string;
    accountNumber?: string;
  };
  hasCharges?: boolean;
  onSuccess?: (updatedApplication: any) => void;
  onClose?: () => void;
}

const LoanEditModal: React.FC<LoanEditModalProps> = ({
  applicationId,
  loan,
  hasCharges = false,
  onSuccess,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<LoanEditFormData>({
    resolver: zodResolver(loanEditSchema),
    defaultValues: {
      loanAmount: loan.loanAmount || 0,
      interestRate: loan.interestRate || 0,
      dueDate: loan.dueDate || '',
      commencementDate: loan.commencementDate || '',
      adminFee: loan.adminFee || 0,
      reason: '',
      invoiceNumber: '',
    }
  });

  // Reset form when loan data changes
  useEffect(() => {
    reset({
      loanAmount: loan.loanAmount || 0,
      interestRate: loan.interestRate || 0,
      dueDate: loan.dueDate || '',
      commencementDate: loan.commencementDate || '',
      adminFee: loan.adminFee || 0,
      reason: '',
      invoiceNumber: '',
    });
  }, [loan, reset]);

  const closeModal = () => {
    const modalElement = document.getElementById('LoanEditModal');
    if (modalElement) {
      const modalInstance = Modal.getInstance(modalElement);
      modalInstance?.hide();
    }
    onClose?.();
  };

  const onSubmit = async (data: LoanEditFormData) => {
    if (!applicationId) {
      toast.error('Application ID is missing');
      console.error('LoanEditModal: applicationId is empty or undefined:', applicationId);
      return;
    }

    if (hasCharges) {
      toast.error('Cannot edit a loan that has charges applied to it');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        loanAmount: data.loanAmount,
        loanTerms: calculateTermDays(data.commencementDate, data.dueDate),
        loanPurpose: data.reason || 'Loan update',
        interestRate: data.interestRate,
        adminFee: data.adminFee,
        dueDate: data.dueDate,
        commencementDate: data.commencementDate,
        securityType: 'UNSECURED' as const,
        calculationMode: 'CUSTOM' as const,
        customLoanType: 'ONCE_OFF' as const,
        repaymentFrequency: 'MONTHLY' as const,
      };

      console.log('LoanEditModal: Submitting to applicationId:', applicationId);
      console.log('LoanEditModal: Payload:', JSON.stringify(payload, null, 2));

      const updatedApplication = await updateLoanDetails(applicationId, payload);

      // Close the edit modal first
      closeModal();

      // Show Sage invoice alert
      const result = await Swal.fire({
        title: 'Update Sage Invoice',
        html: `
          <p class="text-muted mb-3">Loan details have been updated successfully. Please update the corresponding Sage invoice.</p>
          ${data.invoiceNumber ? `<p><strong>Invoice Number:</strong> ${data.invoiceNumber}</p>` : ''}
          <div class="alert alert-info text-start mt-3">
            <small>
              <strong>Updated values:</strong><br/>
              Loan Amount: <strong>${formatCurrency(data.loanAmount)}</strong><br/>
              Interest Rate: <strong>${data.interestRate}%</strong><br/>
              Admin Fee: <strong>${formatCurrency(data.adminFee)}</strong><br/>
              Due Date: <strong>${formatDate(data.dueDate)}</strong>
            </small>
          </div>
        `,
        icon: 'info',
        confirmButtonText: 'Acknowledged',
        confirmButtonColor: '#0d6efd',
        allowOutsideClick: false,
      });

      if (result.isConfirmed) {
        toast.success('Loan details updated successfully');
      }

      onSuccess?.(updatedApplication);
    } catch (error: any) {
      console.error('Error updating loan details:', error);
      const message = error?.response?.data?.message || error?.message || 'Failed to update loan details';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate term in days between two dates
  const calculateTermDays = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 30;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 30;
  };

  return (
    <div className="modal-content rounded-0 border-0 shadow-lg">
      {/* Header */}
      <div className="modal-header border-bottom pb-0" style={{ padding: '2rem 2rem 1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
        <div className="w-100">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#e7f3ff', border: '1px solid rgba(0,123,255,0.2)' }}>
                <i className="fa fa-edit fa-2x text-primary" style={{ display: 'block' }}></i>
              </div>
              <div>
                <h4 className="modal-title mb-0 text-dark fw-bold">Update Loan</h4>
                <p className="text-muted mb-0 mt-1 small">
                  {loan.customerName || 'Update loan details'}
                  {loan.accountNumber && <span className="ms-2 badge bg-light text-dark">{loan.accountNumber}</span>}
                </p>
              </div>
            </div>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          {/* Current values summary */}
          <div className="row g-2 mb-2">
            <div className="col-4">
              <div className="rounded-3 p-2 border bg-white text-center">
                <div className="text-muted" style={{ fontSize: '0.7rem' }}>Current Amount</div>
                <div className="fw-bold text-primary small">{formatCurrency(loan.loanAmount || 0)}</div>
              </div>
            </div>
            <div className="col-4">
              <div className="rounded-3 p-2 border bg-white text-center">
                <div className="text-muted" style={{ fontSize: '0.7rem' }}>Interest Rate</div>
                <div className="fw-bold text-primary small">{loan.interestRate || 0}%</div>
              </div>
            </div>
            <div className="col-4">
              <div className="rounded-3 p-2 border bg-white text-center">
                <div className="text-muted" style={{ fontSize: '0.7rem' }}>Admin Fee</div>
                <div className="fw-bold text-primary small">{formatCurrency(loan.adminFee || 0)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="modal-body p-4" style={{ backgroundColor: '#f8f9fa', maxHeight: '60vh', overflowY: 'auto' }}>
        {/* Charges warning */}
        {hasCharges && (
          <div className="alert alert-danger border-0 mb-4" role="alert">
            <div className="d-flex align-items-center">
              <i className="fa fa-exclamation-triangle fa-lg me-3"></i>
              <div>
                <strong>Cannot Edit Loan</strong>
                <p className="mb-0 small">This loan has charges applied to it and cannot be edited. Please resolve or remove charges first.</p>
              </div>
            </div>
          </div>
        )}

        <form id="loanEditForm" onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={hasCharges}>
            {/* Financial Details */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3">
                  <i className="fa fa-money me-2 text-primary"></i>
                  Financial Details
                </h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold mb-1">
                      Loan Amount (ZAR) <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text"><i className="fa fa-money"></i></span>
                      <input
                        type="number"
                        className={`form-control ${errors.loanAmount ? 'is-invalid' : ''}`}
                        placeholder="0.00"
                        step="0.01"
                        min="0.01"
                        {...register('loanAmount', { valueAsNumber: true })}
                      />
                      {errors.loanAmount && <div className="invalid-feedback">{errors.loanAmount.message}</div>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold mb-1">
                      Interest Rate (%) <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text"><i className="fa fa-percent"></i></span>
                      <input
                        type="number"
                        className={`form-control ${errors.interestRate ? 'is-invalid' : ''}`}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        max="100"
                        {...register('interestRate', { valueAsNumber: true })}
                      />
                      {errors.interestRate && <div className="invalid-feedback">{errors.interestRate.message}</div>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold mb-1">
                      Admin Fee (ZAR)
                    </label>
                    <div className="input-group">
                      <span className="input-group-text"><i className="fa fa-file-text-o"></i></span>
                      <input
                        type="number"
                        className={`form-control ${errors.adminFee ? 'is-invalid' : ''}`}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        {...register('adminFee', { valueAsNumber: true })}
                      />
                      {errors.adminFee && <div className="invalid-feedback">{errors.adminFee.message}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3">
                  <i className="fa fa-calendar me-2 text-info"></i>
                  Dates
                </h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold mb-1">
                      Commencement Date <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text"><i className="fa fa-calendar-check-o"></i></span>
                      <input
                        type="date"
                        className={`form-control ${errors.commencementDate ? 'is-invalid' : ''}`}
                        {...register('commencementDate')}
                      />
                      {errors.commencementDate && <div className="invalid-feedback">{errors.commencementDate.message}</div>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold mb-1">
                      Due Date <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text"><i className="fa fa-calendar"></i></span>
                      <input
                        type="date"
                        className={`form-control ${errors.dueDate ? 'is-invalid' : ''}`}
                        {...register('dueDate')}
                      />
                      {errors.dueDate && <div className="invalid-feedback">{errors.dueDate.message}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice & Reason */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3">
                  <i className="fa fa-file-text me-2 text-secondary"></i>
                  Additional Information
                </h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold mb-1">Invoice Number</label>
                    <div className="input-group">
                      <span className="input-group-text"><i className="fa fa-hashtag"></i></span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., INV-2026-001"
                        {...register('invoiceNumber')}
                      />
                    </div>
                    <small className="text-muted">Sage invoice reference for this loan</small>
                  </div>
                  <div className="col-md-12">
                    <label className="form-label fw-semibold mb-1">Reason for Update (Optional)</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      placeholder="Briefly explain why these loan details are being updated"
                      {...register('reason')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
        </form>
      </div>

      {/* Footer */}
      <div className="modal-footer border-0 px-4 pb-4" style={{ backgroundColor: '#f8f9fa' }}>
        <button
          type="button"
          className="btn btn-outline-secondary px-4 py-2 rounded-pill"
          data-bs-dismiss="modal"
          disabled={isLoading}
        >
          <i className="fa fa-times me-2"></i>
          Cancel
        </button>
        <button
          type="submit"
          form="loanEditForm"
          className="btn btn-primary px-4 py-2 rounded-pill shadow-sm fw-semibold"
          disabled={isLoading || hasCharges}
          style={{ minWidth: '160px' }}
        >
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            <>
              <i className="fa fa-check-circle me-2"></i>
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default LoanEditModal;
